import { Box, ButtonBase, Divider, Popover, styled } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AppAvatar from "components/avatars/AppAvatar";
import useAuth from "hooks/useAuth";
import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { fetchExecutorCabinets, IExecutorCabinet } from "request/ExecutorRequest";
import { UserRole } from "request/UserRequest";

const Trigger = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  padding: "8px 10px",
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: "background-color 150ms ease, border-color 150ms ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.grey[300],
  },
}));

const MenuItem = styled(ButtonBase)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  padding: "8px 10px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.text.primary,
  gap: 10,
  "&:hover": { backgroundColor: theme.palette.action.hover },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
}));

const SectionLabel = styled(Box)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  padding: "4px 10px 6px",
}));

const SidebarProfile: FC = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [cabinets, setCabinets] = useState<IExecutorCabinet[]>([]);

  // Преподаватель может вести несколько предметов: на каждый заведён свой
  // кабинет, между ними переключаемся подписанной ссылкой. Список тянем при
  // первом открытии меню — на каждой загрузке страницы он не нужен.
  useEffect(() => {
    if (!open || user?.role !== UserRole.EXECUTOR || cabinets.length > 0) return;

    fetchExecutorCabinets()
      .then(setCabinets)
      .catch(() => setCabinets([]));
  }, [open, user, cabinets.length]);

  const fullName = useMemo(() => {
    if (!user) return "User";
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.email || "User";
  }, [user]);

  const role =
    user?.role === UserRole.EXECUTOR
      ? "Преподаватель"
      : user?.role === UserRole.CUSTOMER
      ? "Клиент"
      : "";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Fragment>
      <Trigger ref={ref} onClick={() => setOpen(true)} disableRipple>
        <AppAvatar
          src={user?.avatar || "/static/avatar/001-man.svg"}
          sx={{ width: 32, height: 32 }}
        />
        <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {fullName}
          </Box>
          {role && (
            <Box
              sx={{
                fontSize: 11,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.3,
              }}
            >
              {role}
            </Box>
          )}
        </Box>
        <KeyboardArrowUpRoundedIcon
          sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }}
        />
      </Trigger>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={ref.current}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { mb: 0.75, width: 220 } }}
      >
        <Box sx={{ p: 0.75 }}>
          {cabinets.length > 1 && (
            <Fragment>
              <SectionLabel>Кабинеты</SectionLabel>
              {cabinets.map((cabinet) => (
                <MenuItem
                  key={cabinet.executorId}
                  onClick={() => {
                    if (!cabinet.switchUrl) return;
                    window.location.href = cabinet.switchUrl;
                  }}
                >
                  <MenuBookRoundedIcon />
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "left",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {cabinet.subject || "Без предмета"}
                  </Box>
                  {cabinet.isCurrent && <CheckRoundedIcon />}
                </MenuItem>
              ))}
              <Divider sx={{ my: 0.5 }} />
            </Fragment>
          )}

          <MenuItem
            onClick={() => {
              navigate("/dashboard/account");
              setOpen(false);
            }}
          >
            <SettingsRoundedIcon />
            Настройки
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={() => {
              handleLogout();
              toast.success("Вы вышли из аккаунта");
            }}
          >
            <LogoutRoundedIcon />
            Выход
          </MenuItem>
        </Box>
      </Popover>
    </Fragment>
  );
};

export default SidebarProfile;
